import type { FinishedStageRow, FinishedWork } from "@fieldpath/shared";
import { FINISHED_MAX_ROWS, mdyDay } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { daysBefore, finishedCsv, localDay } from "../finishedWork";
import { HelpButton } from "./HelpButton";
import { KindTag } from "./KindTag";
import { useTaskHover } from "./TaskHoverCard";
import type { TaskHoverBind } from "./TaskHoverCard";

/**
 * FINISHED WORK (punch list F117, Jeremy 30 Oct 2026): the stages I finished, between two of my
 * own days, narrowed by a search over the task, project, deliverable and client, and downloaded
 * for Excel. Opened from My work's "Done in the last 30 days" (F112). Only ever the reader's own
 * work: the API takes no person.
 */

/** The quick ranges, in days back from today (today included). */
const QUICK: readonly { readonly label: string; readonly days: number }[] = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "12 months", days: 365 },
];

export function FinishedWorkPage({ fetcher, onOpenTask }: { fetcher: Fetcher; onOpenTask: (taskId: string) => void }) {
  const { bind, card } = useTaskHover(fetcher);
  const today = new Date();
  const [from, setFrom] = useState(() => daysBefore(today, 29));
  const [through, setThrough] = useState(() => localDay(today));
  const [search, setSearch] = useState("");
  const [data, setData] = useState<FinishedWork | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  // Typing in the search waits a moment, so each keystroke is not a request.
  useEffect(() => {
    if (!from || !through) return;
    let live = true;
    const timer = window.setTimeout(() => {
      api
        .myFinished(fetcher, from, through, search)
        .then((d) => {
          if (!live) return;
          setData(d);
          setError(null);
        })
        .catch((e: Error) => live && setError(e.message));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [fetcher, from, through, search, back]);

  const quick = (days: number) => {
    setFrom(daysBefore(new Date(), days - 1));
    setThrough(localDay(new Date()));
  };
  const quickOn = (days: number) => through === localDay(today) && from === daysBefore(today, days - 1);

  const rows = data?.rows ?? [];

  return (
    <>
      {card}
      <div className="count-line">
        {data ? `${rows.length} ${rows.length === 1 ? "STAGE" : "STAGES"} FINISHED` : " "}
        {data?.more ? ` · THE NEWEST ${FINISHED_MAX_ROWS}` : ""}
      </div>
      <h1 className="page-h1">
        Finished work
        <HelpButton screens="finished" />
      </h1>

      <div className="filters">
        <input
          type="search"
          value={search}
          placeholder="Search task, project, deliverable or client…"
          aria-label="Search"
          onChange={(e) => setSearch(e.target.value)}
          className="filter-search"
        />
        <div className="filter-row">
          <div className="viewpick" role="group" aria-label="Finished in the last">
            {QUICK.map((q) => (
              <button
                key={q.days}
                className={`seg${quickOn(q.days) ? " on" : ""}`}
                aria-pressed={quickOn(q.days)}
                onClick={() => quick(q.days)}
              >
                {q.label}
              </button>
            ))}
          </div>
          <label className="finished-date">
            <span className="muted">From</span>
            <input type="date" value={from} max={through || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="finished-date">
            <span className="muted">Through</span>
            <input type="date" value={through} min={from || undefined} onChange={(e) => setThrough(e.target.value)} />
          </label>
          <button
            type="button"
            className="finished-download"
            disabled={rows.length === 0}
            onClick={() => download(rows, from, through)}
            title="The rows shown, as a file Excel opens"
          >
            Download for Excel
          </button>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {data?.more ? (
        <p className="admin-warn">
          More than {FINISHED_MAX_ROWS} stages were finished in these days; the newest {FINISHED_MAX_ROWS} are
          shown. Pick fewer days, or search, to see the rest.
        </p>
      ) : null}

      {!data && !error ? (
        <p className="muted">Loading…</p>
      ) : data && rows.length === 0 ? (
        <div className="empty">
          {search.trim() ? "Nothing you finished in these days matches that." : "You finished nothing in these days."}
        </div>
      ) : (
        <section className="ledger-group">
          {rows.map((r) => (
            <FinishedRow key={r.taskId + r.stageName + r.completedAt} row={r} onOpen={onOpenTask} hover={bind} />
          ))}
        </section>
      )}
    </>
  );
}

function download(rows: readonly FinishedStageRow[], from: string, through: string): void {
  const blob = new Blob([finishedCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `finished-work-${from}-to-${through}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * One finished stage, in My work's row shape: when on the left, the task and where it sits in
 * the middle, the stage boxed on the right. Used by My work's Done section and by this page.
 */
export function FinishedRow({
  row,
  onOpen,
  hover,
}: {
  row: FinishedStageRow;
  onOpen: (taskId: string) => void;
  hover: TaskHoverBind;
}) {
  return (
    <button className="ledger-row is-done" onClick={() => onOpen(row.taskId)} {...hover(row.taskId, row.taskTitle)}>
      {/* When the stage was finished, on the reader's own calendar. */}
      <span className="ledger-due tone-muted">{mdyDay(row.completedAt)}</span>
      <span className="ledger-mid">
        <span className="ledger-title">
          <KindTag kind="task" size="row" />
          {row.taskTitle}
        </span>
        <span className="work-where">
          <KindTag kind="project" size="crumb" />
          {row.projectName}
          {row.deliverableName ? (
            <>
              <span className="sep"> › </span>
              <KindTag kind="deliverable" size="crumb" />
              <span className="deliv-name">{row.deliverableName}</span>
            </>
          ) : null}
          {row.clientName ? (
            <span className="muted">
              {" · "}
              <span className="client-name">{row.clientName}</span>
            </span>
          ) : null}
        </span>
      </span>
      <span className="ledger-right">
        <span className="stage-box">{row.stageName}</span>
      </span>
    </button>
  );
}
